import { SlicesUIElement, html } from '../element/element';
import { StringConverter, BooleanConverter, ObjectConverter } from 'html-element-property-mixins/src/utils/attribute-converters';
import { css } from './image.css.js';
import '../kenburns-animation/kenburns-animation.js';

/**
 * 
 * @element slicesui-image
 * 
 * @fires loaded-changed - Fires when 'loaded' value changed
 * 
 * @prop {String} placeholder - Placeholder image
 * 
 * @prop {Array} sources - Array of imagees
 * 
 * @prop {Boolean} [preload=false] - If true, sources are loaded
 * 
 * @prop {Boolean} [loaded=false] - Reflects loading state of loaded image
 * 
 * @prop {Boolean} [error =false]- Reflects error state of loaded image
 * 
 * @prop {Object} animation - Ken Burns Animation instructions
 * 
 * @prop {String} animationName - Animation name
 * @attr {String}  animation-name
 * 
 * @prop {Boolean}  animationEnabled - If true, animation runs
 * @attr {Boolean}  animation-enabled
 * 
 * @prop {String}  animationKeyframes - CSS Keyframe rules for animation
 * @attr {String}  animation-keyframes 
 * 
 * 
 * @cssprop {Color} --slicesui-image-background-color - Background color
 * @cssprop {cover | contain } --slicesui-image-object-fit - Object fit
 * @cssprop --slicesui-image-object-position - Object position
 * 
 */
class SlicesUIImage extends SlicesUIElement {
  
  /**
   * @private
   */
  get template() {
    return html`
      <style>${css}</style>

      <link rel="stylesheet" href="data:text/css;charset=UTF-8,${this.animationKeyframes}">
      <figure>
        <img
          id="placeholder"
          .src="${this.placeholder || ''}"
          ?hidden="${!this.placeholder}"
        >
        ${this._srcset ? html`
          <img
            id="full"
            .srcset="${this._srcset}"
            @load="${this._handleLoaded}"
            @error="${this._handleError}"
            decoding="async"
            loading="lazy"
            ?hidden="${!this.preload}"
          >
        ` : html``
}
      </figure>

      <kenburns-animation
        ?active="${this.animationEnabled}"
        .zoom="${this.animation.zoom}"
        .panX="${this.animation.xPosition}"
        .panY="${this.animation.yPosition}"
        @value-changed="${this._handleAnimationChanged}"
      ></kenburns-animation>
    `;
  }

  static get properties() {
    return {

      placeholder: {
        observe: true,
        DOM: true,
        fromAttributeConverter: StringConverter.fromAttribute
      },

      sources: {
        observe: true,
        DOM: true,
        fromAttributeConverter: ObjectConverter.fromAttribute
      },

      preload: {
        observe: true,
        DOM: true,
        fromAttributeConverter: BooleanConverter.fromAttribute
      },

      loaded: {
        observe: true,
        DOM: true,
        reflect: true,
        fromAttributeConverter: BooleanConverter.fromAttribute,
        toAttributeConverter: BooleanConverter.toAttribute,
        changedHandler: '_loadedChanged'
      },

      error: {
        observe: true,
      },

      animation: {
        observe: true,
        DOM: true,
        fromAttributeConverter: ObjectConverter.fromAttribute
      },

      animationName: {
        observe: true,
        DOM: true,
        attributeName: 'animation-name',
        fromAttributeConverter: StringConverter.fromAttribute
      },

      animationEnabled: {
        observe: true,
        DOM: true,
        attributeName: 'animation-enabled',
        fromAttributeConverter: BooleanConverter.fromAttribute,
        changedHandler: '_animationEnabledChanged'
      },

      animationKeyframes: {
        observe: true,
        DOM: true,
        attributeName: 'animation-keyframes',
        fromAttributeConverter: StringConverter.fromAttribute
      },

    };
  }

  constructor() {
    super();
    this.placeholder = '';
    this.preload = false;
    this.loaded = false;
    this.error = false;
    this.animation = {};
    this.animationEnabled = false;
    window.addEventListener('online', this._handleOnline.bind(this));
  }

  static get propertiesChangedHandlers() {
    return {
      _toggleOpacity: ['loaded', 'animationEnabled'],
      render: ['placeholder', 'sources', 'loaded', '_srcset', 'preload', 'animationEnabled', 'animationName', 'animation', 'animationKeyframes']
    };
  }

  /**
   * @private
   */
  propertiesChangedCallback(propNames, oldValues, newValues) {
    super.propertiesChangedCallback(propNames, oldValues, newValues);
    this._updateCSS();
  }

  set placeholder(placeholder) {
    if((placeholder || '').substr(0, 4) === 'http') return;
    this['#placeholder'] = placeholder;
  }

  get _srcset() {
    if(!this.preload) return undefined;
    const images = this.sources;
    if(!images) return this.loaded = false;
    const srcset = [];
    images.map((image) => {
      if(image.size == '750w') image.size = '480w';
      if(image.size == '1024w') image.size = '1080w';
      return srcset.push(image.url + ' ' + image.size);
    });
    return srcset.join(', ');
  }

  _updateCSS() {
    this.updateStyles('--slicesui-image-animation-name', this.animationName);
    this.updateStyles('--slicesui-image-animation-play-state', (this.animationEnabled) ? 'running' : 'paused');
    this.updateStyles('--slicesui-image-animation-duration', this.animation.duration + 's');
    this.updateStyles('--slicesui-image-animation-direction', this.animation.direction);
  }

  _loadedChanged() {
    this.dispatchEvent(new CustomEvent('loaded-changed', {detail: {value: this.loaded}}));
  }

  _handleOnline() {
    if(this.error) {
      const $img = this.shadowRoot.querySelector('#full');
      $img.removeAttribute('srcset');
      $img.setAttribute('srcset', this._srcset);
    }
  }

  _handleLoaded() {
    if(!this.sources) return;
    this.loaded = true;
    this.error = false;
  }

  _handleError() {
    this.error = true;
  }

  _handleAnimationChanged(evt) {
    this.animationName = evt.detail.name;
    this.animationKeyframes = evt.detail.keyframes;
  }

  /**
   * Fixes a Safari bug causing the image to be invisible while computed css opacity value equals 1.
   */
  _toggleOpacity() {
    if(!this.animationEnabled) return;
    const img = this.shadowRoot.querySelector('#full');
    if(!img) return;
    img.style.setProperty('opacity', 0.99);
    window.requestAnimationFrame(() => img.style.setProperty('opacity', 1));
  }

}

window.customElements.define('slicesui-image', SlicesUIImage);
